import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { fetchJson } from '../../apiBase';
import { getStudentAvatar } from '../../shared/studentAvatar';
import './AdminPointsPage.css';
import TeacherPointAwards from './TeacherPointAwards';
import StudentPointAvatar from './StudentPointAvatar';

const HISTORY_PAGE_SIZE = 40;
const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Istanbul',
});

function authHeaders() {
  try {
    const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function accountAwardStorageKey(schoolId, actorId) {
  try {
    const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
    return `teacher_point_award:account:${schoolId}:${encodeURIComponent(actorId || user.user_id || user.phone || user.id || user.sub || 'current')}`;
  } catch { return `teacher_point_award:account:${schoolId}:${encodeURIComponent(actorId || 'current')}`; }
}

async function getJson(path) {
  const response = await fetchJson(path, { headers: authHeaders() });
  const body = await response.json();
  if (!response.ok) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Puan bilgileri yüklenemedi.');
  return body;
}

function AnimatedPoints({ value, animationKey }) {
  const amount = Math.max(0, Number(value) || 0);
  const [displayed, setDisplayed] = useState(0);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || amount === 0) {
      setDisplayed(amount);
      return undefined;
    }
    let frame;
    const started = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - started) / 750);
      setDisplayed(Math.round(amount * (1 - (1 - progress) ** 3)));
      if (progress < 1) frame = window.requestAnimationFrame(tick);
    };
    setDisplayed(0);
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [amount, animationKey]);

  return <span aria-label={`Bugün ${amount} puan`} aria-live="off">+{displayed} <small>bugün</small></span>;
}

const StudentAvatar = StudentPointAvatar;

function groupTransactions(items) {
  const groups = new Map();
  (items || []).forEach((item) => {
    const date = new Date(item.occurred_at);
    const label = Number.isNaN(date.getTime()) ? 'Tarih bilinmiyor' : dateFormatter.format(date);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(item);
  });
  return Array.from(groups.entries());
}

export default function AdminPointsPage({ schoolId, canAward = false, awardActorId }) {
  const loadRoster = useCallback(() => getJson(`/admin/points/${encodeURIComponent(schoolId)}/students`), [schoolId]);
  const loadHistory = useCallback((studentId, limit, offset) => getJson(`/parent/points?student_id=${encodeURIComponent(studentId)}&limit=${limit}&offset=${offset}`), []);
  const awardApi = useMemo(() => canAward && schoolId ? {
    storageKey: accountAwardStorageKey(schoolId, awardActorId),
    submit: async (body) => {
      const response = await fetchJson(`/admin/points/${encodeURIComponent(schoolId)}/point-awards`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) { const error = new Error('İşlem doğrulanamadı.'); error.status = response.status; error.code = result.detail?.code; throw error; }
      return result;
    },
  } : null, [canAward, schoolId, awardActorId]);
  return <StudentPointsBoard loadRoster={schoolId ? loadRoster : null} loadHistory={loadHistory} awardApi={awardApi} />;
}

export function StudentPointsBoard({ loadRoster, loadHistory, classScoped = false, onBack, awardApi }) {
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [historyOffset, setHistoryOffset] = useState(0);
  const [appreciationStudent, setAppreciationStudent] = useState(null);

  useEffect(() => {
    if (!loadRoster) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    loadRoster()
      .then((body) => { if (active) setRoster(body); })
      .catch((requestError) => { if (active) setError(requestError.message || 'Puanlar yüklenemedi.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadRoster, reloadKey]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let active = true;
    setDetailLoading(true);
    setDetailError('');
    loadHistory(selectedId, HISTORY_PAGE_SIZE, historyOffset)
      .then((body) => {
        if (!active) return;
        setDetail((previous) => historyOffset === 0 ? body : {
          ...body,
          transactions: [...(previous?.transactions || []), ...body.transactions],
        });
      })
      .catch((requestError) => { if (active) setDetailError(requestError.message || 'Puan geçmişi yüklenemedi.'); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [selectedId, historyOffset, reloadKey, loadHistory]);

  const students = useMemo(() => roster?.students || [], [roster]);
  const selectedStudent = students.find((student) => String(student.student_id) === String(selectedId));
  const classNames = useMemo(() => [...new Set(students.map((student) => student.class_name).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'tr')), [students]);
  const visible = students.filter((student) => {
    if (classFilter && student.class_name !== classFilter) return false;
    const text = `${student.first_name} ${student.last_name} ${student.student_no} ${student.class_name || ''}`.toLocaleLowerCase('tr');
    return text.includes(query.trim().toLocaleLowerCase('tr'));
  });
  const totalToday = students.reduce((sum, student) => sum + Number(student.today_points || 0), 0);
  const earningToday = students.filter((student) => Number(student.today_points || 0) > 0).length;

  const openStudent = (studentId) => {
    setSelectedId(studentId);
    setHistoryOffset(0);
    setDetail(null);
  };

  return (
    <Box className={`admin-points-page${classScoped ? ' ap-teacher-board' : ''}`}>
      <main className="ap-main">
        <header className="ap-heading">
          <Box className="ap-heading__start">
            {onBack && <IconButton className="ap-heading__back" onClick={onBack} aria-label="Öğretmen Moduna Dön" title="Öğretmen Moduna Dön"><ArrowBackRounded /></IconButton>}
            <Box className="ap-heading__copy">
              <Typography component="h1">Öğrenci Puanları</Typography>
              <Typography>Öğrencilerin bugün kazandığı puanları ve puan geçmişini görün.</Typography>
            </Box>
          </Box>
          <Button variant="outlined" startIcon={<RefreshRounded />} disabled={loading} onClick={() => { setHistoryOffset(0); setDetail(null); setReloadKey((value) => value + 1); }}>
            Yenile
          </Button>
        </header>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {awardApi && <TeacherPointAwards api={awardApi} students={students} appreciationStudent={appreciationStudent} boardProfile={classScoped}
          onAppreciationClose={() => setAppreciationStudent(null)} onAward={() => { setHistoryOffset(0); setReloadKey(value => value + 1); }} />}
        <section className="ap-hero" aria-label="Bugünkü puan özeti">
          <Box className="ap-hero__icon" aria-hidden="true">🪙</Box>
          <Box className="ap-hero__copy">
            <Typography className="ap-eyebrow">{roster?.day ? roster.day.split('-').reverse().join('.') : 'Bugün'}</Typography>
            <Typography component="h2">Bugün biriken puanlar</Typography>
            <Typography>Her öğrencinin günlük kazanımı kartında görünür. Ayrıntılar için karta dokunun.</Typography>
          </Box>
          <Box className="ap-hero__metrics">
            <Box><strong>{totalToday}</strong><span>bugün kazanılan</span></Box>
            <Box><strong>{earningToday}</strong><span>puan kazanan öğrenci</span></Box>
            <Box><strong>{students.length}</strong><span>öğrenci</span></Box>
          </Box>
        </section>

        <section className="ap-panel" aria-label="Öğrenci puanları">
          <Box className="ap-panel__head">
            <Box><Typography component="h2">Öğrenciler</Typography><Typography>Avatarına dokunarak puanlarını inceleyin.</Typography></Box>
            <Box className="ap-filters">
              <TextField size="small" label="Öğrenci ara" value={query} onChange={(event) => setQuery(event.target.value)}
                InputProps={{ startAdornment: <SearchRounded sx={{ color: '#8191b1', mr: 0.7 }} /> }} />
              {!classScoped && <TextField size="small" select label="Şube" value={classFilter} onChange={(event) => setClassFilter(event.target.value)}>
                <MenuItem value="">Tüm şubeler</MenuItem>
                {classNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </TextField>}
            </Box>
          </Box>
          {loading ? <Box className="ap-student-grid">{Array.from({ length: 8 }, (_, index) => <Skeleton key={index} variant="rounded" height={205} />)}</Box>
            : visible.length ? <Box className="ap-student-grid">
              {visible.map((student) => {
                const theme = getStudentAvatar(student.student_id);
                const name = `${student.first_name} ${student.last_name}`.trim();
                return <button className="ap-student-card" style={{ background: theme.bgColor, borderColor: theme.borderColor }}
                  key={student.student_id} type="button" onClick={() => openStudent(student.student_id)} aria-label={`${name} puan geçmişini aç`}>
                  <StudentAvatar student={student} />
                  <strong>{name}</strong>
                  <span className={`ap-today-points${student.today_points > 0 ? ' has-points' : ''}`}>
                    <AnimatedPoints value={student.today_points} animationKey={reloadKey} />
                  </span>
                </button>;
              })}
            </Box> : <Box className="ap-empty">Bu arama veya şube için öğrenci bulunamadı.</Box>}
        </section>
      </main>

      <Drawer anchor="right" open={Boolean(selectedId)} onClose={() => setSelectedId(null)}
        PaperProps={{ className: 'ap-drawer', role: 'dialog', 'aria-label': 'Öğrenci puan geçmişi' }}>
        {selectedStudent && <>
          <Box className="ap-drawer__header">
            <Box className="ap-drawer__identity"><StudentAvatar student={selectedStudent} large />
              <Box><Typography className="ap-eyebrow">Puanlarım</Typography><Typography component="h2">{selectedStudent.first_name} {selectedStudent.last_name}</Typography>
                <Typography>{selectedStudent.class_name || 'Şube yok'} · No {selectedStudent.student_no}</Typography></Box>
            </Box>
            <IconButton aria-label="Puan geçmişini kapat" onClick={() => setSelectedId(null)}><CloseRounded /></IconButton>
          </Box>
          <Box className="ap-drawer__body">
            <Box className="ap-balance-grid">
              <Box className="ap-balance ap-balance--primary"><span>Kullanılabilir puan</span><strong>{detail?.available_points ?? selectedStudent.available_points}</strong></Box>
              <Box className="ap-balance"><span>Toplam kazanılan</span><strong>{detail?.total_earned_points ?? selectedStudent.total_earned_points}</strong></Box>
            </Box>
            <Box className="ap-today-detail">🪙 Bugün <strong>+{selectedStudent.today_points} puan</strong> kazandı</Box>
            {awardApi && <Button className="ap-appreciation" variant="outlined" fullWidth onClick={() => { setAppreciationStudent(selectedStudent); setSelectedId(null); }}>👏 Öğretmen Takdiri</Button>}
            <Box className="ap-history-head"><Typography component="h3">Puan geçmişi</Typography><Typography>En yeni işlemler önce gösterilir.</Typography></Box>
            {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
            {detailLoading && !detail && <Box sx={{ display: 'grid', gap: 1 }}><Skeleton height={58} /><Skeleton height={58} /><Skeleton height={58} /></Box>}
            {detail && detail.transactions.length === 0 && <Box className="ap-empty">Henüz puan işlemi yok.</Box>}
            {detail && groupTransactions(detail.transactions).map(([day, transactions]) => <section className="ap-history-group" key={day}>
              <Typography component="h4">{day}</Typography>
              {transactions.map((item) => <Box className="ap-history-row" key={item.id}>
                <span>{item.label}{item.note && <small className="ap-history-detail">{item.note}</small>}{item.activity_title && <small className="ap-history-detail">{item.activity_title}</small>}</span><strong className={item.amount >= 0 ? 'is-earned' : 'is-spent'}>{item.amount > 0 ? '+' : ''}{item.amount}</strong>
              </Box>)}
            </section>)}
            {detail?.has_more && <Button variant="outlined" fullWidth disabled={detailLoading} onClick={() => setHistoryOffset((value) => value + HISTORY_PAGE_SIZE)}>
              {detailLoading ? 'Yükleniyor…' : 'Daha fazla göster'}
            </Button>}
          </Box>
        </>}
      </Drawer>
    </Box>
  );
}
